import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { Platform, Pressable, StyleSheet, View } from "@-/com.npmjs.react-native";
import { getGlossSizing } from "#sizing";
import { type ReactNode, useCallback } from "react";
import { copyToClipboard } from "@-/dev.improve.haniverse.lib.clipboard";
import { cn } from "@-/com.npmjs.cn";
import { styled } from "styled-components/native";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@-/dev.improve.haniverse.components.ui.context-menu";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { isCardCompleted } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.file.completion";
import type { MorphemeGlyphTileStage } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { MORPHEME_STAGE_BACKGROUND_CLASSES } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.variants";
import { MorphemeDetailsTileGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.details";
import { CARD_TILE_TYPE_LABELS, CARD_TILE_TYPE_ICONS, type CardTileType } from "#card-tile-type";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  CardDetailsTileDueLine,
  DUE_LINE_PADDING_TOP_PX,
  getDueLineFontSize,
} from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile._components.due-line";
import { CardDetailsTileBackContent } from "#card-details-tile-back-content";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";

export type CardDetailsTileUiProps = {
  /** Distinguishes a meaning card ("Meaning") from a pronunciation card ("Pronounce"). */
  cardType: CardTileType;
  className?: string;
  /**
   * Compact due-time token from {@link formatCardDueLabel}: `"5d"` / `"3h"` /
   * `"5m"` for a scheduled card, `"Due"` once it is due, and `undefined` when the
   * card has no schedule yet. Expanded into the corner line below the card type
   * ("Due in 5d" / "Due now" / "No due date").
   */
  dueLabel?: string;
  graphemeCard: MorphemeDetailsTileSlice;
  /**
   * `graphemeCard` is a provisional slice built from the local actor DB (glyph, due, actor gloss)
   * while the content/static-DB details are still loading. Shows "…" where the answer text is
   * not known yet and suspends the authoring-completion fade (completion is a content-DB fact).
   */
  isDetailsPending?: boolean;
  isEditing?: boolean;
  isLoading?: boolean;
  isMastered?: boolean;
  /** Whether this exact user card is part of the app-wide selection. */
  isSelected?: boolean;
  onGraphemePress?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  onLongPress?: () => void;
  onPress?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  onRemoveCard?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  /**
   * The card's own tone-number reading, for a pronunciation card. The tile shows this reading and
   * tone-colours its glyph by it; without it the tile follows the character's main reading, which
   * misreads a character's other readings (干 gan4 beside 干 gan1).
   */
  pinyin?: string | null;
  size: number;
  /**
   * The grapheme's SRS tile stage, resolved by the caller's single batched
   * `useMorphemeListStagesQuery` and passed in. Mandatory so this tile fires NO
   * per-tile stage query — see {@link MorphemeDetailsTileUi} for why the deck's
   * grid lifts this to one batched query at the parent.
   */
  stage: MorphemeGlyphTileStage;
  testID?: string;
};

// Prefer this split, but cap the front height below so small tiles retain enough
// space for two gloss lines, the due label, padding, divider and borders.
const FRONT_HALF_HEIGHT_RATIO = 0.45;
// The corner card-type icon sizes as a fraction of the card width so it scales
// with the tile (the "transform: scale" feel), with a readability floor for the
// thumbnail-sized phone card.
const CARD_TYPE_ICON_RATIO = 0.11;
const CARD_TYPE_ICON_MIN_PX = 12;

/**
 * Query container that sizes its children's `cqi` units off the card's own width.
 * The card already declares itself as a proportional widget via the `size` prop;
 * `cqi`-based labels keep that contract: text scales with the tile, not with the
 * viewport.
 */
const CardTileQueryContainer = styled(View)`
  flex: 1;
  container-type: inline-size;
`;

/**
 * A deck/study *card* tile, split by a central divider into a "front" half (top) and a gloss
 * half (bottom). The front half shows the character glyph in the top-left and, in the top-right,
 * the card type ("Meaning" / "Pronounce") above a due line ("Due in 5d" / "Due now" /
 * "No due date"). The bottom half shows the gloss. Unlike the morpheme grid (one tile per
 * morpheme), the deck renders one of these per card — meaning + pronunciation.
 */
export function CardDetailsTileUi({
  cardType,
  className,
  dueLabel,
  graphemeCard,
  isDetailsPending = false,
  isEditing = false,
  isLoading,
  isMastered = false,
  isSelected = false,
  onGraphemePress,
  onLongPress,
  onPress,
  onRemoveCard,
  pinyin = null,
  size,
  // Paint the tile with the same review-stage background as the morpheme glyph
  // buttons (`MorphemeDetailsTileUi`) — a familiarity wash that deepens with
  // mastery — so a card and its morpheme read as the same study object. `unadded`
  // maps to `bg-surface` (pure white in light mode), so an un-studied grapheme
  // stays white on search / levels surfaces instead of the beige page wash.
  stage: morphemeStage,
  testID,
}: CardDetailsTileUiProps) {
  const [fadeMissingMnemonicEnabled] = useDeviceSetting("fadeMissingMnemonicEnabled");
  const dueLineFontSize = getDueLineFontSize(size);

  const stageFillClassName = MORPHEME_STAGE_BACKGROUND_CLASSES[morphemeStage];
  const fillClassName = isMastered
    ? MORPHEME_STAGE_BACKGROUND_CLASSES.mastered
    : stageFillClassName;
  // Completion is a content-DB fact, so it is unknown while the details are pending — don't
  // fade a tile that may well be authored.
  const fadeMissingMnemonicClassName =
    fadeMissingMnemonicEnabled && !isDetailsPending && !isCardCompleted(graphemeCard)
      ? "opacity-25"
      : undefined;

  // A proportional split alone leaves less than 32px for the two gloss lines on
  // phone tiles. Include pb-1 (4px), the due line's top padding, the divider
  // (1px) and both button borders (2px) in the space reserved below.
  const minimumBackHeight =
    2 * getGlossSizing(size).lineHeight + dueLineFontSize + 4 + DUE_LINE_PADDING_TOP_PX + 1 + 2;
  const frontHalfHeight = Math.max(
    1,
    Math.floor(Math.min(size * FRONT_HALF_HEIGHT_RATIO, size - minimumBackHeight)),
  );
  // The corner card-type icon overlays the front half (absolutely positioned in
  // the top-left), so the centered glyph can claim the full front-half height —
  // no label line above it to subtract anymore.
  const glyphAvailableHeight = frontHalfHeight;
  const cardTypeIconSize = Math.max(CARD_TYPE_ICON_MIN_PX, Math.round(size * CARD_TYPE_ICON_RATIO));

  const handlePress = useCallback(() => {
    if (isEditing) {
      return;
    }
    onGraphemePress?.(graphemeCard);
    onPress?.(graphemeCard);
  }, [graphemeCard, isEditing, onGraphemePress, onPress]);

  const withWebContextMenu = (children: ReactNode) => {
    if (Platform.OS !== "web") {
      return children;
    }
    // ContextMenu's root view shrinks to content on web unless it is explicitly
    // stretched inside the square tile wrapper from CardDetailsTileList.
    return (
      <View style={StyleSheet.absoluteFill}>
        <ContextMenu style={StyleSheet.absoluteFill}>
          <ContextMenuTrigger asChild>
            <View style={StyleSheet.absoluteFill}>{children}</View>
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem
              onPress={() => {
                void copyToClipboard(graphemeCard.front, { label: graphemeCard.front });
              }}
            >
              <Text>Copy {graphemeCard.front}</Text>
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      </View>
    );
  };

  return withWebContextMenu(
    <CardTileQueryContainer>
      <Button
        accessibilityLabel={`${graphemeCard.front}, ${CARD_TILE_TYPE_LABELS[cardType]} card`}
        accessibilityState={{ busy: isLoading === true, selected: isSelected }}
        className={cn(
          // `size="fill"` skips `default`'s `h-10` / `sm:h-9`. `flex-1` grows to
          // the square tile wrapper (required on web inside the context-menu shell).
          // Override the base Button row layout (`flex-row items-center justify-center
          // gap-2`) — the `gap-2` in particular was crushing the stacked halves.
          "min-h-0 w-full flex-1 flex-col items-stretch justify-start gap-0 rounded-xs border border-border p-0",
          fillClassName,
          fadeMissingMnemonicClassName,
          isSelected && "border-1 border-solid border-primary/40",
          className,
        )}
        isLoading={isLoading}
        onLongPress={isEditing ? undefined : onLongPress}
        onPress={handlePress}
        size="fill"
        testID={testID}
        variant="ghost"
      >
        {/* Front (top) half: glyph centered, with the card-type icon pinned to the
            top-left corner. A fixed height (rather than flex-1) places the divider
            AND gives the bottom half a definite height so its gloss/pinyin can
            resolve its own flex sizing instead of collapsing. */}
        <View
          className="min-h-0 min-w-0 flex-col items-center justify-center self-stretch px-1.5 pt-1"
          style={{ height: frontHalfHeight }}
        >
          <View className="min-h-0 min-w-0 flex-1 flex-row items-center justify-center self-stretch">
            <MorphemeDetailsTileGlyph
              align="center"
              availableHeight={glyphAvailableHeight}
              graphemeCard={graphemeCard}
              readingToneNumbersPinyin={pinyin}
            />
          </View>
          {/* Wrap in an absolutely-positioned View — uniwind position classes on
              the SVG itself don't take it out of flex flow, so the icon must be
              pinned to the corner by its container. */}
          <View className="absolute top-1 left-1">
            <Icon
              className="text-muted-foreground"
              name={CARD_TILE_TYPE_ICONS[cardType]}
              size={cardTypeIconSize}
            />
          </View>
        </View>
        {/* Divider. `mx-2` insets it from the side edges; a muted-foreground tint
            keeps it visible against the stage fill (plain `border` washes out). */}
        <View className="mx-2 h-px bg-muted-foreground/40" />
        {/* Back (bottom) half: the gloss (meaning) / reading (pronunciation) fills and
            centers; the due line sits just below it, pinned to the bottom. */}
        <View className="min-h-0 min-w-0 flex-1 flex-col items-stretch self-stretch px-1.5 pb-1">
          <CardDetailsTileBackContent
            cardType={cardType}
            graphemeCard={graphemeCard}
            isDetailsPending={isDetailsPending}
            pinyin={pinyin}
            size={size}
          />
          <CardDetailsTileDueLine dueLabel={dueLabel} isMastered={isMastered} size={size} />
        </View>
        {isEditing && onRemoveCard ? (
          <Pressable
            className="absolute top-1.5 right-1.5 size-6 items-center justify-center rounded-full border border-border bg-background"
            hitSlop={8}
            onPress={() => {
              onRemoveCard(graphemeCard);
            }}
          >
            <Text className="text-xs font-semibold text-foreground">X</Text>
          </Pressable>
        ) : null}
      </Button>
    </CardTileQueryContainer>,
  );
}
